import { MaterialModel, MaterialsAPIItem } from '@common/models';
import MaterialService from '@core/services/MaterialsService';
import { useFetchMaterialsQuery } from '@ee-api/materials-api/materialsService';
import { MaterialsQueryParams } from '@ee-api/queryParams';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useStores } from '@ee-api/stores-api/useStores';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { createSelector } from '@reduxjs/toolkit';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';
import { skipToken } from '@reduxjs/toolkit/query';

import { AxiosBaseQueryFunction } from '../../axiosBaseQuery';

type useMaterialsReturn = TypedUseQueryHookResult<MaterialsAPIItem[], MaterialsQueryParams, AxiosBaseQueryFunction> & {
    materials: MaterialModel[];
    isFetchingMaterials: boolean;
    isMaterialLoaded: boolean;
};

const emptyArray = [] satisfies MaterialModel[] | MaterialsAPIItem[];

export const parseMaterialsApiResults = createSelector(
    (data?: MaterialsAPIItem[]) => data,
    (data) => (!!data && data.length > 0 ? MaterialService._parseAPIResponse(data) : emptyArray)
);

const useMaterials = (): useMaterialsReturn => {
    const zipcode = useAppSelector(selectZipcode);
    const { isFetchingStores } = useStores();
    const { selectedValue: selectedStore } = useSelectedStore();

    const { materials, isMaterialLoaded, ...rest } = useFetchMaterialsQuery(
        !isFetchingStores && zipcode && selectedStore?.companyId
            ? { params: { zipCode: zipcode, storeId: selectedStore.companyId } }
            : skipToken,
        {
            selectFromResult: (result) => ({
                materials:
                    result.isError || !zipcode || !selectedStore ? emptyArray : parseMaterialsApiResults(result.data),
                isMaterialLoaded: [!result.isUninitialized, !result.isFetching, result?.data?.length].every(Boolean),
                ...result,
            }),
        }
    );

    return {
        materials,
        isFetchingMaterials:
            rest.isFetching ||
            (!!zipcode &&
                !!selectedStore &&
                (zipcode !== rest.originalArgs?.params?.zipCode ||
                    selectedStore.companyId !== rest.originalArgs?.params?.storeId)),
        isMaterialLoaded,
        ...rest,
    };
};

export default useMaterials;
